/**
 * WL-K7 — Advisor-Export-Cockpit (Cross-Mandant-DATEV).
 *
 * Eine Kanzlei exportiert in EINEM Arbeitsgang für N Mandanten — je Mandant getrennte
 * Artefakte (DATEV-inhärent). Mandanten-Tabelle × Zeitraum; pro Mandant EXTF-CSV,
 * Belege-ZIP und (gegated) Eingangsbelege-ZIP; „Alle exportieren" als sequenzielle,
 * pro-Mandant fehler-isolierte Schleife.
 *
 * 🔴 CROSS-CONTAMINATION-GARANTIE (Kern des Moduls): JEDER Mandanten-Export ist
 * AUSSCHLIESSLICH über dessen `scopeUid` parametrisiert. Es gibt KEINEN geteilten,
 * über Schleifen-Iterationen leckenden Zustand:
 *   - Daten: `loadMandantDatevSource(mandant.scopeUid, …)` — Query `userId == scopeUid`.
 *   - Stammdaten (BeraterNr/MandantenNr/SKR): `getClientDatevConfig(mandant.scopeUid)`,
 *     pro Export FRISCH geladen und in einen funktions-lokalen `DatevHeaderConfig`
 *     gegossen. Nie aus dem eigenen `sellerData` oder aus `effectiveScopeUid`.
 *   - GUIDs: aus `${collection}:${docId}` der jeweils MANDANTEN-eigenen Belege
 *     abgeleitet (`buildDatevExport`) → keine Kollision/kein Leak zwischen Mandanten.
 * Damit trägt A's CSV/ZIP niemals B's Daten oder Nummern (K7-1/K7-2/K7-4/R-K7).
 *
 * SICHERHEIT: rein kosmetisch beta-gated (`isLoopBBeta`). Die harte Autorisierung
 * (Advisor-Membership + Loop-B-Beta + Cross-Tenant-Isolation) liegt server-seitig in
 * den Callables/Functions (`getClientDatevConfig`, `buildDatevBelegePaket`) — ein
 * Direktaufruf für einen fremden Mandanten scheitert dort mit `permission-denied`.
 *
 * Outer/Inner-Split: die äußere Komponente ist das Sichtbarkeits-Gate; die innere
 * (datenladende) Komponente wird nur für Beta-Nutzer montiert.
 *
 * @module AdvisorDatevPage
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconCsv,
  IconFileArchive,
  IconInbox,
  IconAdvisorDatev,
  IconSuccess,
  IconWarning,
  IconSpinner,
} from '../icons';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';

import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { canUseAdvisorDatev } from '../config/kanzleiAccess';
import { canUseInboundDatev } from '../config/inboundDatevAccess';
import type { AvailableTenant } from '../types/tenantTypes';
import type { InboundDocument } from '../types/inboundTypes';
import { getClientDatevConfig } from '../api/clientDatevApi';
import { isExportReady, type ClientDatevConfigView } from '../utils/clientDatevFormat';
import { loadMandantDatevSource } from '../utils/datev/datevSource';
import {
  buildDatevExport,
  buildBelegeCsvLines,
  buildDatevFilename,
  type DatevHeaderConfig,
} from '../utils/datevExport';
import type { SkrKontenrahmen } from '../utils/datevMapping';
import { buildDatevBlob } from '../utils/datevCp1252';
import { downloadBlob } from '../api/invoiceApi';
import { downloadDatevBelegePaket } from '../api/datevBelegeApi';
import { buildInboundDatevRefs } from '../utils/inboundDatevRefs';
import { downloadInboundDatevPaket } from '../api/inboundDatevApi';
import logger from '../utils/logger';

// ─── Datum-Helfer ────────────────────────────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Heutiges Datum als ISO YYYY-MM-DD (lokale Zeitzone). */
function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** 1. Januar des laufenden Jahres als ISO YYYY-MM-DD (Default-Periodenbeginn). */
function getYearStartISO(): string {
  return `${new Date().getFullYear()}-01-01`;
}

/**
 * Read-Storm-Schutz für {@link loadInboundDocsForScope}. Wird die Query am
 * Limit gekappt, ist der Zeitraum evtl. NICHT vollständig abgedeckt — der
 * Aufrufer erhält das über `truncated` zurück und muss es sichtbar machen
 * (WL-K7 Zeitfenster-Fix, s. Modul-Änderungslog `changelog-loop-b-inbound-vat.md`).
 */
const INBOUND_DOCS_QUERY_LIMIT = 500;

/** Ergebnis von {@link loadInboundDocsForScope}. */
interface InboundDocsPage {
  docs: InboundDocument[];
  /**
   * `true`, wenn die Query exakt `INBOUND_DOCS_QUERY_LIMIT` Treffer lieferte —
   * es könnten WEITERE Dokumente im gewählten Zeitraum existieren, die nicht
   * zurückgegeben wurden. Der Aufrufer MUSS das dem Nutzer sichtbar machen
   * (nie eine stille Teilmenge als vollständig ausgeben).
   */
  truncated: boolean;
}

/**
 * Lädt die `inbound_documents` EINES Scopes, servereitig auf `[fromISO, toISO]`
 * gefiltert (WL-K7 Zeitfenster-Fix — vormals wurde mit festem `limit(500)` +
 * `orderBy(receivedAt desc)` geladen und CLIENTSEITIG auf den Zeitraum gefiltert;
 * bei vielen neueren Dokumenten außerhalb des gewählten Zeitraums konnte das
 * Ergebnis für ältere/engere Zeiträume unvollständig/falsch sein, ohne dass das
 * sichtbar war). Der Range-Filter liegt auf DEMSELBEN Feld wie `orderBy`
 * (`receivedAt`) — der bestehende Composite-Index `userId ASC, receivedAt DESC`
 * deckt diese Query bereits ab, es ist KEIN neuer Firestore-Index nötig.
 *
 * Explizit über `scopeUid` parametrisiert — kein ambienter Scope (Cross-
 * Contamination-Garantie, s. Modul-Kopf).
 *
 * @param scopeUid Scope-uid (Mandant).
 * @param fromISO  Periodenbeginn (inklusive), `YYYY-MM-DD`.
 * @param toISO    Periodenende (inklusive), `YYYY-MM-DD`.
 */
async function loadInboundDocsForScope(
  scopeUid: string,
  fromISO: string,
  toISO: string,
): Promise<InboundDocsPage> {
  const fromTs = Timestamp.fromDate(new Date(`${fromISO}T00:00:00`));
  const toTs = Timestamp.fromDate(new Date(`${toISO}T23:59:59.999`));
  const q = query(
    collection(db, 'inbound_documents'),
    where('userId', '==', scopeUid),
    where('receivedAt', '>=', fromTs),
    where('receivedAt', '<=', toTs),
    orderBy('receivedAt', 'desc'),
    limit(INBOUND_DOCS_QUERY_LIMIT),
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<InboundDocument, 'id'>) }));
  return { docs, truncated: docs.length === INBOUND_DOCS_QUERY_LIMIT };
}

/**
 * Baut den `DatevHeaderConfig` aus der FRISCH geladenen Mandanten-Konfiguration.
 * Wirft, wenn die Stammdaten nicht exportbereit sind (Ampel-Block) — KEIN stiller
 * Skip, KEIN Fallback auf leere/fremde Nummern.
 */
function buildMandantHeader(
  config: ClientDatevConfigView,
  fromISO: string,
  toISO: string,
): DatevHeaderConfig {
  return {
    beraterNr: config.datevBeraterNr,
    mandantenNr: config.datevMandantenNr,
    skr: (config.datevSkr as SkrKontenrahmen | null) ?? 'SKR03',
    sachkontenLaenge: config.datevSachkontenLaenge ?? 4,
    fromISO,
    toISO,
    generatedAtISO: new Date().toISOString(),
  };
}

// ─── Zustands-Typen ────────────────────────────────────────────────────────────────

type ExportKind = 'csv' | 'belege' | 'inbound';

/** Ampel-/Konfig-Zustand je Mandant (nur Anzeige; Export lädt die Konfig frisch). */
type MandantConfigState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; config: ClientDatevConfigView; ready: boolean };

/** Laufzeit-Zustand des Exports je Mandant. */
interface MandantExportState {
  /** Aktuell laufender Export-Typ (deaktiviert die Zeile), sonst `null`. */
  running: ExportKind | null;
  /** Letzte Rückmeldung (Fehler/Block/Skip/Erfolg) oder `null`. */
  message: { tone: 'error' | 'blocked' | 'info' | 'done'; text: string } | null;
  /** „zuletzt exportiert" (Epoch ms) oder `null`. */
  lastExportedAt: number | null;
}

const EMPTY_EXPORT_STATE: MandantExportState = {
  running: null,
  message: null,
  lastExportedAt: null,
};

// ─── Innere (datenladende) Komponente ──────────────────────────────────────────────

const AdvisorDatevInner: React.FC = () => {
  const { t } = useTranslation('common');
  const { availableTenants } = useAuth();

  // 🔴 K7-5: Mandanten-Reihenfolge deterministisch = `availableTenants`-Order.
  // Cross-Mandant-Kohorte = fremde Tenants mit Advisor-Rolle (der eigene implizite
  // Tenant des Advisors ist KEIN Mandant dieses Cockpits).
  const mandants: AvailableTenant[] = useMemo(
    () => availableTenants.filter((tenant) => tenant.role === 'advisor'),
    [availableTenants],
  );

  const [fromISO, setFromISO] = useState<string>(() => getYearStartISO());
  const [toISO, setToISO] = useState<string>(() => getTodayISO());
  const [configByScope, setConfigByScope] = useState<Record<string, MandantConfigState>>({});
  const [exportByTenant, setExportByTenant] = useState<Record<string, MandantExportState>>({});
  const [batchRunning, setBatchRunning] = useState(false);

  // Ampel-Konfiguration je Mandant laden (nur Anzeige). Fehler bleibt pro Zeile isoliert.
  useEffect(() => {
    let cancelled = false;
    setConfigByScope((prev) => {
      const next: Record<string, MandantConfigState> = {};
      for (const m of mandants) {
        next[m.scopeUid] = prev[m.scopeUid] ?? { status: 'loading' };
      }
      return next;
    });
    // Extracted OUTSIDE the for-loop body (no-loop-func lint nit, WL-K7 FIX v3 §4):
    // eslint's no-loop-func flags closures declared *inside* a loop that reference an
    // outer `let` reassigned after the loop (`cancelled`). Hoisting the per-mandant
    // fetch into a named function called FROM the loop (instead of defined IN it)
    // removes the syntactic nesting the rule flags — behavior is unchanged.
    const loadOneMandant = (m: AvailableTenant): void => {
      getClientDatevConfig(m.scopeUid)
        .then((res) => {
          if (cancelled) return;
          setConfigByScope((prev) => ({
            ...prev,
            [m.scopeUid]: {
              status: 'ready',
              config: res.config,
              ready: isExportReady(res.config),
            },
          }));
        })
        .catch((err) => {
          if (cancelled) return;
          logger.error('AdvisorDatevPage: config load failed', err);
          setConfigByScope((prev) => ({ ...prev, [m.scopeUid]: { status: 'error' } }));
        });
    };
    for (const m of mandants) {
      loadOneMandant(m);
    }
    return () => {
      cancelled = true;
    };
  }, [mandants]);

  const patchExport = useCallback((tenantId: string, patch: Partial<MandantExportState>): void => {
    setExportByTenant((prev) => ({
      ...prev,
      [tenantId]: { ...(prev[tenantId] ?? EMPTY_EXPORT_STATE), ...patch },
    }));
  }, []);

  /**
   * EXTF-Buchungsstapel (CSV) EINES Mandanten. Alle Datenquellen sind funktions-lokal
   * und ausschließlich über `mandant.scopeUid` parametrisiert (Cross-Contamination-
   * Garantie, s. Modul-Kopf). Fehler werden pro Mandant abgefangen (Batch-Isolation).
   */
  const exportCsv = useCallback(
    async (mandant: AvailableTenant): Promise<void> => {
      patchExport(mandant.tenantId, { running: 'csv', message: null });
      try {
        // 1. Stammdaten FRISCH + autoritativ je Mandant (403 hier bleibt isoliert).
        //    §19-Status kommt server-autoritativ als Sibling mit (GA-Gate #17, FIX v3).
        const { config, isSmallBusiness } = await getClientDatevConfig(mandant.scopeUid);
        // 2. Ampel-Block: fehlende/ungültige BeraterNr/MandantenNr ⇒ KEIN stiller Skip.
        if (!isExportReady(config)) {
          patchExport(mandant.tenantId, {
            running: null,
            message: { tone: 'blocked', text: t('advisorDatev.blocked', 'Stammdaten unvollständig — bitte im Cockpit ergänzen.') },
          });
          return;
        }
        // 3. Header aus DIESES Mandanten Nummern.
        const header = buildMandantHeader(config, fromISO, toISO);
        // 4. Daten scoped auf DIESEN Mandanten, mit DIESES Mandanten §19-Status.
        //    RK-8: `travel` (bereits settled/cancelled-gefiltert, RK-6-Loader) wird
        //    OHNE zusätzliches Client-Gate mitgereicht — Sichtbarkeit im Sammelexport
        //    ist bewusst nicht vom eigenen `canUseTravelExpenses` der Kanzlei abhängig
        //    (§7.3 architecture-reisekosten.md: Pauschal-Buchungen der Mandanten
        //    erscheinen automatisch im Kanzlei-EXTF-Stapel).
        const { invoices, expenses, travel } = await loadMandantDatevSource(
          mandant.scopeUid,
          isSmallBusiness,
        );
        // 5. Reiner Build + CP1252-Download.
        const result = buildDatevExport(invoices, expenses, header, undefined, travel);
        const blob = buildDatevBlob(result.csvLines);
        downloadBlob(blob, buildDatevFilename(header));
        patchExport(mandant.tenantId, {
          running: null,
          lastExportedAt: Date.now(),
          message: {
            tone: 'done',
            text: t('advisorDatev.csvDone', '{{count}} Buchungen exportiert.', { count: result.bookedCount }),
          },
        });
      } catch (err) {
        logger.error('AdvisorDatevPage: CSV export failed', err);
        patchExport(mandant.tenantId, {
          running: null,
          message: { tone: 'error', text: err instanceof Error ? err.message : String(err) },
        });
      }
    },
    [patchExport, fromISO, toISO, t],
  );

  /** Belege-Paket (ZIP) EINES Mandanten (Muster `DatevExportPage`, per-Mandant scoped). */
  const exportBelege = useCallback(
    async (mandant: AvailableTenant): Promise<void> => {
      patchExport(mandant.tenantId, { running: 'belege', message: null });
      try {
        const { config, isSmallBusiness } = await getClientDatevConfig(mandant.scopeUid);
        if (!isExportReady(config)) {
          patchExport(mandant.tenantId, {
            running: null,
            message: { tone: 'blocked', text: t('advisorDatev.blocked', 'Stammdaten unvollständig — bitte im Cockpit ergänzen.') },
          });
          return;
        }
        const header = buildMandantHeader(config, fromISO, toISO);
        // RK-8: `travel` mitreichen (s. Begründung in `exportCsv` oben) — Reise-
        // Pauschalen sind beleglos und tragen ohnehin KEINEN `DatevDocumentRef`
        // (§3.5), beeinflussen also `documentRefs`/den ZIP-Pfad unterhalb nicht.
        const { invoices, expenses, travel } = await loadMandantDatevSource(
          mandant.scopeUid,
          isSmallBusiness,
        );
        const result = buildDatevExport(invoices, expenses, header, undefined, travel);

        // Nur Refs MIT erwarteter Datei an die Function (P5).
        const availableRefs = result.documentRefs.filter((ref) => ref.hasFileExpectation === true);
        const availableKeys = new Set(availableRefs.map((ref) => ref.documentKey));

        const { skipped, skippedKeys } = await downloadDatevBelegePaket({
          fromISO: header.fromISO,
          toISO: header.toISO,
          header,
          refs: availableRefs.map((ref) => ({
            collection: ref.collection,
            docId: ref.docId,
            fileKind: ref.fileKind,
            guid: ref.guid,
            filenameInZip: ref.filenameInZip,
          })),
        });

        // finalKeys = availableKeys \ skippedKeys; Truncation ⇒ fail-closed keine Links.
        const truncated = skipped > skippedKeys.length;
        const skippedSet = new Set(skippedKeys);
        const finalKeys = truncated
          ? new Set<string>()
          : new Set(Array.from(availableKeys).filter((key) => !skippedSet.has(key)));

        const csvLines = buildBelegeCsvLines(result, header, finalKeys);
        downloadBlob(buildDatevBlob(csvLines), buildDatevFilename(header));

        patchExport(mandant.tenantId, {
          running: null,
          lastExportedAt: Date.now(),
          message:
            skipped > 0
              ? { tone: 'info', text: t('advisorDatev.belegeSkipped', '{{count}} Beleg(e) ohne Datei übersprungen.', { count: skipped }) }
              : { tone: 'done', text: t('advisorDatev.belegeDone', 'Belege-Paket erstellt.') },
        });
      } catch (err) {
        logger.error('AdvisorDatevPage: Belege export failed', err);
        patchExport(mandant.tenantId, {
          running: null,
          message: { tone: 'error', text: err instanceof Error ? err.message : String(err) },
        });
      }
    },
    [patchExport, fromISO, toISO, t],
  );

  /** Eingangsbelege-Paket (ZIP) EINES Mandanten — nur gerendert, wenn `canUseInboundDatev`. */
  const exportInbound = useCallback(
    async (mandant: AvailableTenant): Promise<void> => {
      patchExport(mandant.tenantId, { running: 'inbound', message: null });
      try {
        const { config } = await getClientDatevConfig(mandant.scopeUid);
        if (!isExportReady(config)) {
          patchExport(mandant.tenantId, {
            running: null,
            message: { tone: 'blocked', text: t('advisorDatev.blocked', 'Stammdaten unvollständig — bitte im Cockpit ergänzen.') },
          });
          return;
        }
        const header = buildMandantHeader(config, fromISO, toISO);
        // WL-K7 Zeitfenster-Fix: der Zeitraum-Filter läuft jetzt server-seitig
        // (Firestore where-Range auf `receivedAt`) — kein clientseitiges
        // Nachfiltern mehr. `truncated` warnt sichtbar, statt eine stille
        // Teilmenge als vollständig auszugeben.
        const { docs, truncated } = await loadInboundDocsForScope(mandant.scopeUid, fromISO, toISO);
        const refs = buildInboundDatevRefs(docs);
        if (refs.length === 0) {
          patchExport(mandant.tenantId, {
            running: null,
            message: { tone: 'info', text: t('advisorDatev.inboundEmpty', 'Keine Eingangsbelege im Zeitraum.') },
          });
          return;
        }
        const { skipped } = await downloadInboundDatevPaket({ fromISO, toISO, header, refs });
        patchExport(mandant.tenantId, {
          running: null,
          lastExportedAt: Date.now(),
          message: truncated
            ? {
                tone: 'info',
                text: t(
                  'advisorDatev.inboundTruncated',
                  'Mehr als {{limit}} Eingangsbelege im Zeitraum — nicht alle wurden berücksichtigt. Bitte Zeitraum eingrenzen und erneut exportieren.',
                  { limit: INBOUND_DOCS_QUERY_LIMIT },
                ),
              }
            : skipped > 0
              ? { tone: 'info', text: t('advisorDatev.inboundSkipped', '{{count}} Eingangsbeleg(e) übersprungen.', { count: skipped }) }
              : { tone: 'done', text: t('advisorDatev.inboundDone', 'Eingangsbelege übergeben.') },
        });
      } catch (err) {
        logger.error('AdvisorDatevPage: inbound export failed', err);
        patchExport(mandant.tenantId, {
          running: null,
          message: { tone: 'error', text: err instanceof Error ? err.message : String(err) },
        });
      }
    },
    [patchExport, fromISO, toISO, t],
  );

  /**
   * „Alle exportieren" = SEQUENZIELLE Schleife über `mandants` (K7-5-Order). Jeder
   * `exportCsv` fängt seine Fehler selbst ab ⇒ ein 403 bei Mandant B bricht den Batch
   * NICHT ab (K7-3): A und C werden vollständig exportiert, B bleibt isoliert markiert.
   */
  const exportAll = useCallback(async (): Promise<void> => {
    if (batchRunning || mandants.length === 0) return;
    setBatchRunning(true);
    try {
      for (const mandant of mandants) {
        // Bewusst sequenziell (await in der Schleife) — je Mandant getrennte Downloads,
        // deterministische Reihenfolge, kein paralleler Zustands-Overlap.
        // eslint-disable-next-line no-await-in-loop
        await exportCsv(mandant);
      }
    } finally {
      setBatchRunning(false);
    }
  }, [batchRunning, mandants, exportCsv]);

  const periodValid = fromISO <= toISO;

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6">
          <h1 className="flex items-center gap-3 text-2xl font-bold text-primary">
            <IconAdvisorDatev size={20} className="text-accent-ink" />
            {t('advisorDatev.title', 'DATEV-Sammelexport')}
          </h1>
          <p className="mt-1 text-sm text-muted">
            {t('advisorDatev.subtitle', 'Exportieren Sie für alle Mandanten in einem Arbeitsgang — je Mandant getrennte DATEV-Artefakte.')}
          </p>
        </header>

        {/* Zeitraum + Sammel-Aktion */}
        <div className="mb-6 flex flex-col gap-4 rounded-xl border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.6)] p-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-faint">{t('advisorDatev.from', 'Von')}</span>
              <input
                type="date"
                value={fromISO}
                max={toISO}
                onChange={(e) => setFromISO(e.target.value)}
                aria-label={t('advisorDatev.from', 'Von')}
                className="rounded-lg border border-hairline bg-surface px-3 py-1.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-faint">{t('advisorDatev.to', 'Bis')}</span>
              <input
                type="date"
                value={toISO}
                min={fromISO}
                onChange={(e) => setToISO(e.target.value)}
                aria-label={t('advisorDatev.to', 'Bis')}
                className="rounded-lg border border-hairline bg-surface px-3 py-1.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={exportAll}
            disabled={batchRunning || mandants.length === 0 || !periodValid}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              batchRunning || mandants.length === 0 || !periodValid
                ? 'cursor-not-allowed border border-hairline bg-surface text-gray-600'
                : 'bg-indigo-600 text-white hover:bg-indigo-500'
            }`}
          >
            {batchRunning ? <IconSpinner className="animate-spin" /> : <IconAdvisorDatev />}
            {batchRunning
              ? t('advisorDatev.exportingAll', 'Exportiere alle …')
              : t('advisorDatev.exportAll', 'Alle exportieren (EXTF-CSV)')}
          </button>
        </div>

        {mandants.length === 0 ? (
          <div className="rounded-lg bg-surface-raised px-6 py-10 text-center text-sm text-muted">
            {t('advisorDatev.noMandanten', 'Noch keine Mandanten mit Kanzlei-Zugang.')}
          </div>
        ) : (
          <ul className="space-y-3">
            {mandants.map((mandant) => {
              const cfg = configByScope[mandant.scopeUid] ?? { status: 'loading' as const };
              const exp = exportByTenant[mandant.tenantId] ?? EMPTY_EXPORT_STATE;
              // §3.2b: reiner Advisor-Kontext (Kanzlei-Cockpit) — `isAdvisorContext=true`
              // ⇒ KEIN Owner-Tier-Term (der StB wird nie durch den Ultra-Status seines
              // Mandanten gesperrt). Der Owner-Tier ist hier bewusst nicht geladen und
              // wird ignoriert, deshalb der Platzhalter `'free'`.
              const inboundVisible = canUseInboundDatev(mandant.scopeUid, 'free', true);
              const rowBusy = exp.running !== null || batchRunning;
              const ready = cfg.status === 'ready' && cfg.ready;

              return (
                <li
                  key={mandant.tenantId}
                  className="rounded-xl border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.6)] p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    {/* Mandant + Ampel */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {cfg.status === 'loading' && (
                          <IconSpinner size={16} className="shrink-0 animate-spin text-faint" />
                        )}
                        {cfg.status === 'ready' && ready && (
                          <IconSuccess size={16} className="shrink-0 text-[var(--tone-success-fg)]" />
                        )}
                        {(cfg.status === 'error' || (cfg.status === 'ready' && !ready)) && (
                          <IconWarning size={16} className="shrink-0 text-[var(--tone-warning-fg)]" />
                        )}
                        <span className="truncate text-sm font-semibold text-primary">
                          {mandant.displayName || mandant.tenantId}
                        </span>
                      </div>
                      <div className="mt-1 text-xs">
                        {cfg.status === 'loading' && (
                          <span className="text-faint">{t('advisorDatev.ampelLoading', 'Stammdaten werden geprüft …')}</span>
                        )}
                        {cfg.status === 'error' && (
                          <span className="text-[var(--tone-danger-fg)]">{t('advisorDatev.ampelError', 'Stammdaten konnten nicht geladen werden.')}</span>
                        )}
                        {cfg.status === 'ready' && ready && (
                          <span className="text-[var(--tone-success-fg)]">
                            {t('advisorDatev.ampelReady', 'Exportbereit — Berater {{berater}} / Mandant {{mandant}}', {
                              berater: cfg.config.datevBeraterNr,
                              mandant: cfg.config.datevMandantenNr,
                            })}
                          </span>
                        )}
                        {cfg.status === 'ready' && !ready && (
                          <span className="text-[var(--tone-warning-fg)]">
                            {t('advisorDatev.ampelNotReady', 'Stammdaten unvollständig — Export blockiert.')}
                          </span>
                        )}
                      </div>
                      {/* WJ-Hinweis (e-rechnung E-1): DATEV nimmt WJ-Beginn 01.01. an. */}
                      <div className="mt-1 text-[11px] leading-snug text-faint">
                        {t('advisorDatev.wjHint', 'Wirtschaftsjahr-Beginn 01.01. angenommen — bei abweichendem WJ Export in DATEV anpassen.')}
                      </div>
                    </div>

                    {/* Aktionen */}
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => exportCsv(mandant)}
                        disabled={rowBusy}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-500/60 px-3 py-1.5 text-xs font-medium text-accent-ink transition-colors hover:bg-indigo-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {exp.running === 'csv' ? <IconSpinner className="animate-spin" /> : <IconCsv />}
                        {t('advisorDatev.csv', 'EXTF-CSV')}
                      </button>
                      <button
                        type="button"
                        onClick={() => exportBelege(mandant)}
                        disabled={rowBusy}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-strong px-3 py-1.5 text-xs font-medium text-secondary transition-colors hover:bg-surface-inset disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {exp.running === 'belege' ? <IconSpinner className="animate-spin" /> : <IconFileArchive />}
                        {t('advisorDatev.belege', 'Belege-ZIP')}
                      </button>
                      {inboundVisible && (
                        <button
                          type="button"
                          onClick={() => exportInbound(mandant)}
                          disabled={rowBusy}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-strong px-3 py-1.5 text-xs font-medium text-secondary transition-colors hover:bg-surface-inset disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {exp.running === 'inbound' ? <IconSpinner className="animate-spin" /> : <IconInbox />}
                          {t('advisorDatev.inbound', 'Eingangsbelege-ZIP')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Rückmeldung + „zuletzt exportiert" */}
                  {(exp.message || exp.lastExportedAt) && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      {exp.message && (
                        <span
                          className={
                            exp.message.tone === 'error'
                              ? 'text-[var(--tone-danger-fg)]'
                              : exp.message.tone === 'blocked'
                                ? 'text-[var(--tone-warning-fg)]'
                                : exp.message.tone === 'done'
                                  ? 'text-[var(--tone-success-fg)]'
                                  : 'text-muted'
                          }
                        >
                          {exp.message.text}
                        </span>
                      )}
                      {exp.lastExportedAt && (
                        <span className="text-faint">
                          {t('advisorDatev.lastExported', 'Zuletzt exportiert: {{time}}', {
                            time: new Date(exp.lastExportedAt).toLocaleString('de-DE'),
                          })}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-faint">
          {t('advisorDatev.hint', 'Jeder Mandant wird als eigene DATEV-Datei exportiert (getrennte Berater-/Mandantennummer). Import in DATEV: Buchungsstapel bzw. Belegtransfer je Mandant.')}
        </p>
      </div>
    </div>
  );
};

/**
 * Äußeres Sichtbarkeits-Gate. Rendert das Cockpit NUR für Loop-B-Beta-Nutzer; alle
 * anderen sehen einen neutralen Hinweis (der Server erzwingt den echten Zugriff —
 * dies ist rein kosmetisch, s. Modul-Kopf).
 */
const AdvisorDatevPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, sellerData } = useAuth();

  if (!canUseAdvisorDatev(currentUser?.uid, sellerData)) {
    return (
      <div className="min-h-screen bg-surface p-6">
        <p className="text-sm text-muted">
          {t('advisorDatev.unavailable', 'Diese Funktion ist für Ihr Konto nicht verfügbar.')}
        </p>
      </div>
    );
  }
  return <AdvisorDatevInner />;
};

export default AdvisorDatevPage;
